import { useEffect, useState } from "react";
import { groupsFor, positionLabel, sortRoster, isSetter } from "@/lib/dvStats";
import { useT } from "@/lib/i18n";
import Counter from "./Counter";

export default function MobileBoard({ match, stats, flash, readOnly, bump, t }) {
  const { lang } = useT();
  const [pid, setPid] = useState(match.roster[0]?.player_id);
  useEffect(() => {
    if (!match.roster.some((p) => p.player_id === pid)) setPid(match.roster[0]?.player_id);
  }, [match.roster, pid]);

  const player = match.roster.find((p) => p.player_id === pid);
  if (!player) return null;
  const s = stats.players[player.player_id];

  return (
    <div className="lg:hidden flex flex-col gap-2" data-testid="dv-mobile-board">
      <div className="grid grid-cols-3 gap-1.5" data-testid="dv-player-chips">
        {sortRoster(match.roster).map((p) => {
          const active = p.player_id === pid;
          return (
            <button
              key={p.player_id}
              type="button"
              onClick={() => setPid(p.player_id)}
              data-testid={`dv-chip-${p.number}`}
              aria-pressed={active}
              className={`dv-btn min-h-[52px] px-2 py-1.5 rounded-xl border-2 flex items-center gap-2 text-left ${
                active
                  ? "bg-sunset border-sunset text-white shadow-[0_0_0_3px_rgba(255,140,66,0.35)]"
                  : "bg-white border-slate-200 text-slate-900"
              }`}
            >
              <span className={`font-heading font-black text-lg leading-none shrink-0 ${active ? "text-white" : "text-ocean"}`}>{p.number}</span>
              <span className="text-xs font-bold leading-tight line-clamp-2 min-w-0">{p.name}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl bg-white border border-slate-200 shadow-soft px-3 py-2 flex items-center justify-between gap-3" data-testid="dv-mobile-player">
        <div className="min-w-0">
          <p className="font-heading font-black text-lg text-slate-900 leading-none truncate">
            #{player.number} {player.name}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">{positionLabel(player.position, lang)}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {isSetter(player) ? (
            <Pct label={t("dv_set_pct")} value={s.setPct} cls="bg-emerald-50 text-emerald-700" testid={`dv-m-setpct-${player.number}`} />
          ) : (
            <Pct label={t("dv_rec_pct")} value={s.receptionPct} cls="bg-emerald-50 text-emerald-700" testid={`dv-m-recpct-${player.number}`} />
          )}
          <Pct label={t("dv_att_pct")} value={s.attackPct} cls="bg-rose-50 text-rose-700" testid={`dv-m-attpct-${player.number}`} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {groupsFor(player).map((g) => (
          <div key={g.category} className={`rounded-xl border border-slate-200 overflow-hidden ${g.band}`}>
            <div className={`px-3 py-1 font-heading font-black text-[11px] tracking-widest ${g.header}`}>{t(g.labelKey)}</div>
            <div className={`grid gap-1.5 px-1.5 py-1.5 ${g.types.length === 1 ? "grid-cols-1" : g.types.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
              {g.types.map((ty) => {
                const key = `${g.category}_${ty.type}`;
                return (
                  <div key={key} className="flex flex-col items-center gap-0.5">
                    <span className="text-[10px] font-bold text-slate-700">{t(ty.labelKey)}</span>
                    <Counter
                      value={s[key]}
                      group={g}
                      size="lg"
                      disabled={readOnly}
                      flash={flash === `${player.player_id}_${key}`}
                      onPlus={() => bump(player.player_id, g.category, ty.type, 1)}
                      onMinus={() => bump(player.player_id, g.category, ty.type, -1)}
                      testid={`dv-m-counter-${player.number}-${key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-slate-900 text-white px-3 py-2 grid grid-cols-3 gap-2" data-testid="dv-mobile-team">
        <Team label={t("dv_rec_pct")} value={`${stats.team.receptionPct}%`} />
        <Team label={t("dv_att_pct")} value={`${stats.team.attackPct}%`} />
        <Team label={t("dv_set_pct")} value={`${stats.team.setPct}%`} />
        <Team label={t("dv_aces")} value={stats.team.aces} />
        <Team label={t("dv_blocks")} value={stats.team.blocks} />
        <Team label={t("dv_errors")} value={stats.team.errorsTotal} />
      </div>
    </div>
  );
}

function Pct({ label, value, cls, testid, suffix = "%" }) {
  return (
    <div className={`rounded-lg px-2.5 py-1 text-center ${cls}`} data-testid={testid}>
      <p className="text-[9px] font-bold uppercase tracking-wide">{label}</p>
      <p className="font-heading font-black text-base leading-none">{value}{suffix}</p>
    </div>
  );
}

function Team({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold truncate">{label}</p>
      <p className="font-heading font-black text-lg leading-none">{value}</p>
    </div>
  );
}
